import {css, useTheme} from "@emotion/react";

import {elevation} from "@pg-design/elevation";
import {calculateRemSize, flex, onDesktop, w100} from "@pg-design/helpers-css";
import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {IInfiniteListQuery} from "@pg-design/infinite-list";
import {Text} from "@pg-design/text-module";
import {pluralize} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {hideOfferModal, navToNextModal, navToPrevModal, setModalQueryParams} from "../../state/offer_modals_slice";
import {OfferModalLatestNavType} from "../../types/OfferModalLatestNavType";
import {IOfferModalName} from "../../types/OfferModalName";
import {IOfferModalOffer} from "../../types/OfferModalOffer";
import {OFFER_MODAL_OFFER_OUT_OF_SCOPE_POSITION} from "./constants";
import {useOfferModalOfferWithPosition} from "./use_offer_modal_offers_with_position";

interface IProps {
    offersQuery: IInfiniteListQuery<IOfferModalOffer>;
    selectedOffer: IOfferModalOffer;
    modalName: IOfferModalName;
    onNextOffer?: (offerId: number) => void;
    onPrevOffer?: (offerId: number) => void;
}

const offersPluralize = pluralize(["inwestycja", "inwestycje", "inwestycji"]);

export const OfferModalOfferSwitcher = (props: IProps) => {
    const {offersQuery, modalName, onNextOffer, onPrevOffer} = props;
    const theme = useTheme();
    const {isMobile} = useUserDevice();
    const dispatch = useAppDispatch();
    const latestNavUsed = useAppSelector((state) => state.offerModals[modalName].latestNavUsed);

    const page = offersQuery.page;
    const pageSize = offersQuery.pageSize;

    const {selectedOffer, offersWithPosition, offersStartingPosition} = useOfferModalOfferWithPosition({offers: offersQuery.data, page, pageSize, modalName});

    if (!selectedOffer) {
        return null;
    }

    const nextOfferPosition = selectedOffer.position + 1;
    const nextOffer = offersWithPosition.find((offer) => offer.position === nextOfferPosition);
    const prevOfferPosition = selectedOffer.position - 1;
    const prevOffer = offersWithPosition.find((offer) => offer.position === prevOfferPosition);

    const previousPagesCount = page > 1 ? offersStartingPosition : 0;
    const endOfPageCount = previousPagesCount + pageSize + 1;
    const totalCount = offersQuery.count;
    const readablePosition = selectedOffer.position + 1;

    const isNextOfferPreSale = nextOffer?.data.configuration.pre_sale;
    const isPrevOfferPreSale = prevOffer?.data.configuration.pre_sale;
    const showNextIcon = readablePosition < totalCount && !(isNextOfferPreSale && nextOfferPosition + 1 === totalCount);
    const showPrevIcon = readablePosition > 1 && !(isPrevOfferPreSale && prevOfferPosition <= 0);
    const isSingleOffer = totalCount === 1;
    const isPreSale = selectedOffer.data.configuration.pre_sale;

    const handlers = {
        close: () => {
            dispatch(hideOfferModal({modalName}));
        },
        navToNext: () => {
            if (nextOfferPosition + 1 === endOfPageCount) {
                dispatch(
                    setModalQueryParams({
                        modalName,
                        listingPage: page + 1,
                        listingPageSize: pageSize
                    })
                );
            }

            if (nextOffer) {
                onNextOffer?.(nextOffer.id);
                dispatch(navToNextModal({modalName, offerId: nextOffer.id}));
            }
        },
        navToPrev: () => {
            if (prevOfferPosition + 1 === previousPagesCount) {
                dispatch(
                    setModalQueryParams({
                        modalName,
                        listingPage: page - 1,
                        listingPageSize: pageSize
                    })
                );
            }

            if (prevOffer) {
                onPrevOffer?.(prevOffer.id);
                dispatch(navToPrevModal({modalName, offerId: prevOffer.id}));
            }
        }
    };

    if (isPreSale) {
        if (latestNavUsed === OfferModalLatestNavType.NEXT) {
            handlers.navToNext();
        }
        if (latestNavUsed === OfferModalLatestNavType.PREV) {
            handlers.navToPrev();
        }
    }

    return (
        <div css={wrapper}>
            {!isSingleOffer && selectedOffer.position !== OFFER_MODAL_OFFER_OUT_OF_SCOPE_POSITION && (
                <button type="button" disabled={!showPrevIcon} onClick={handlers.navToPrev} css={navArrowLeft}>
                    <ChevronLeftIcon
                        size={isMobile ? "1.6" : "3.6"}
                        wrapperSize={isMobile ? "3.2" : "6.4"}
                        fill={showPrevIcon ? theme.colors.secondary : theme.colors.gray[700]}
                        wrapperColor={showPrevIcon ? theme.colors.primary : theme.colors.gray[100]}
                    />
                </button>
            )}
            <Text variant="body_copy_2" color={!isMobile ? "white" : undefined} align={!isMobile ? "center" : undefined} css={navTitle}>
                {readablePosition}/{totalCount} {offersPluralize(totalCount)}
            </Text>
            {!isSingleOffer && (
                <button type="button" disabled={!showNextIcon} onClick={handlers.navToNext} css={navArrowRight}>
                    <ChevronRightIcon
                        size={isMobile ? "1.6" : "3.6"}
                        wrapperSize={isMobile ? "3.2" : "6.4"}
                        fill={showNextIcon ? theme.colors.secondary : theme.colors.gray[700]}
                        wrapperColor={showNextIcon ? theme.colors.primary : theme.colors.gray[100]}
                    />
                </button>
            )}
        </div>
    );
};

const wrapper = css`
    position: fixed;
    inset: auto 0 0;
    background-color: #fff;
    ${w100};
    ${flex("center", "center")};
    gap: ${calculateRemSize(3)};
    min-height: ${calculateRemSize(6)};
    max-height: ${calculateRemSize(6)};
    ${elevation(1)};

    ${onDesktop(css`
        background-color: transparent;
        ${elevation(0)};
        position: initial;
        min-height: 0;
        max-height: 0;
    `)}
`;

const navTitle = css`
    ${onDesktop(css`
        position: absolute;
        inset: 0 0 auto 0;
        transform: translateY(-100%);
        line-height: ${calculateRemSize(5)};
    `)}
`;

const navArrowRight = css`
    ${onDesktop(css`
        position: absolute;
        z-index: 20;
        inset: 50% -${calculateRemSize(9)} auto auto;
        transform: translate(100%, -100%);
    `)}
`;

const navArrowLeft = css`
    ${onDesktop(css`
        position: absolute;
        z-index: 20;
        inset: 50% auto auto -${calculateRemSize(9)};
        transform: translate(-100%, -100%);
    `)}
`;
import {css, useTheme} from "@emotion/react";

import {elevation} from "@pg-design/elevation";
import {calculateRemSize, flex, onDesktop, w100} from "@pg-design/helpers-css";
import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {IInfiniteListQuery} from "@pg-design/infinite-list";
import {Text} from "@pg-design/text-module";
import {pluralize} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {hideOfferModal, navToNextModal, navToPrevModal, setModalQueryParams} from "../../state/offer_modals_slice";
import {OfferModalLatestNavType} from "../../types/OfferModalLatestNavType";
import {IOfferModalName} from "../../types/OfferModalName";
import {IOfferModalOffer} from "../../types/OfferModalOffer";
import {OFFER_MODAL_OFFER_OUT_OF_SCOPE_POSITION} from "./constants";
import {useOfferModalOfferWithPosition} from "./use_offer_modal_offers_with_position";

interface IProps {
    offersQuery: IInfiniteListQuery<IOfferModalOffer>;
    selectedOffer: IOfferModalOffer;
    modalName: IOfferModalName;
    onNextOffer?: (offerId: number) => void;
    onPrevOffer?: (offerId: number) => void;
}

const offersPluralize = pluralize(["inwestycja", "inwestycje", "inwestycji"]);

export const OfferModalOfferSwitcher = (props: IProps) => {
    const {offersQuery, modalName, onNextOffer, onPrevOffer} = props;
    const theme = useTheme();
    const {isMobile} = useUserDevice();
    const dispatch = useAppDispatch();
    const latestNavUsed = useAppSelector((state) => state.offerModals[modalName].latestNavUsed);

    const page = offersQuery.page;
    const pageSize = offersQuery.pageSize;

    const {selectedOffer, offersWithPosition, offersStartingPosition} = useOfferModalOfferWithPosition({offers: offersQuery.data, page, pageSize, modalName});

    if (!selectedOffer) {
        return null;
    }

    const nextOfferPosition = selectedOffer.position + 1;
    const nextOffer = offersWithPosition.find((offer) => offer.position === nextOfferPosition);
    const prevOfferPosition = selectedOffer.position - 1;
    const prevOffer = offersWithPosition.find((offer) => offer.position === prevOfferPosition);

    const previousPagesCount = page > 1 ? offersStartingPosition : 0;
    const endOfPageCount = previousPagesCount + pageSize + 1;
    const totalCount = offersQuery.count;
    const readablePosition = selectedOffer.position + 1;

    const isNextOfferPreSale = nextOffer?.data.configuration.pre_sale;
    const isPrevOfferPreSale = prevOffer?.data.configuration.pre_sale;
    const showNextIcon = readablePosition < totalCount && !(isNextOfferPreSale && nextOfferPosition + 1 === totalCount);
    const showPrevIcon = readablePosition > 1 && !(isPrevOfferPreSale && prevOfferPosition <= 0);
    const isSingleOffer = totalCount === 1;
    const isPreSale = selectedOffer.data.configuration.pre_sale;

    const handlers = {
        close: () => {
            dispatch(hideOfferModal({modalName}));
        },
        navToNext: () => {
            if (nextOfferPosition + 1 === endOfPageCount) {
                dispatch(
                    setModalQueryParams({
                        modalName,
                        listingPage: page + 1,
                        listingPageSize: pageSize
                    })
                );
            }

            if (nextOffer) {
                onNextOffer?.(nextOffer.id);
                dispatch(navToNextModal({modalName, offerId: nextOffer.id}));
            }
        },
        navToPrev: () => {
            if (prevOfferPosition + 1 === previousPagesCount) {
                dispatch(
                    setModalQueryParams({
                        modalName,
                        listingPage: page - 1,
                        listingPageSize: pageSize
                    })
                );
            }

            if (prevOffer) {
                onPrevOffer?.(prevOffer.id);
                dispatch(navToPrevModal({modalName, offerId: prevOffer.id}));
            }
        }
    };

    if (isPreSale) {
        if (latestNavUsed === OfferModalLatestNavType.NEXT) {
            handlers.navToNext();
        }
        if (latestNavUsed === OfferModalLatestNavType.PREV) {
            handlers.navToPrev();
        }
    }

    return (
        <div css={wrapper}>
            {!isSingleOffer && selectedOffer.position !== OFFER_MODAL_OFFER_OUT_OF_SCOPE_POSITION && (
                <button type="button" disabled={!showPrevIcon} onClick={handlers.navToPrev} css={navArrowLeft}>
                    <ChevronLeftIcon
                        size={isMobile ? "1.6" : "3.6"}
                        wrapperSize={isMobile ? "3.2" : "6.4"}
                        fill={showPrevIcon ? theme.colors.secondary : theme.colors.gray[700]}
                        wrapperColor={showPrevIcon ? theme.colors.primary : theme.colors.gray[100]}
                    />
                </button>
            )}
            <Text variant="body_copy_2" color={!isMobile ? "white" : undefined} align={!isMobile ? "center" : undefined} css={navTitle}>
                {readablePosition}/{totalCount} {offersPluralize(totalCount)}
            </Text>
            {!isSingleOffer && (
                <button type="button" disabled={!showNextIcon} onClick={handlers.navToNext} css={navArrowRight}>
                    <ChevronRightIcon
                        size={isMobile ? "1.6" : "3.6"}
                        wrapperSize={isMobile ? "3.2" : "6.4"}
                        fill={showNextIcon ? theme.colors.secondary : theme.colors.gray[700]}
                        wrapperColor={showNextIcon ? theme.colors.primary : theme.colors.gray[100]}
                    />
                </button>
            )}
        </div>
    );
};

const wrapper = css`
    position: fixed;
    inset: auto 0 0;
    background-color: #fff;
    ${w100};
    ${flex("center", "center")};
    gap: ${calculateRemSize(3)};
    min-height: ${calculateRemSize(6)};
    max-height: ${calculateRemSize(6)};
    ${elevation(1)};

    ${onDesktop(css`
        background-color: transparent;
        ${elevation(0)};
        position: initial;
        min-height: 0;
        max-height: 0;
    `)}
`;

const navTitle = css`
    ${onDesktop(css`
        position: absolute;
        inset: 0 0 auto 0;
        transform: translateY(-100%);
        line-height: ${calculateRemSize(5)};
    `)}
`;

const navArrowRight = css`
    ${onDesktop(css`
        position: absolute;
        z-index: 20;
        inset: 50% -${calculateRemSize(9)} auto auto;
        transform: translate(100%, -100%);
    `)}
`;

const navArrowLeft = css`
    ${onDesktop(css`
        position: absolute;
        z-index: 20;
        inset: 50% auto auto -${calculateRemSize(9)};
        transform: translate(-100%, -100%);
    `)}
`;
